"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@/lib/utils";

// Generate unique ID if not provided
const generateId = () => `tabs-${Math.random().toString(36).substr(2, 9)}`;

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & {
    id?: string;
  }
>(({ className, id, ...props }, ref) => {
  const [clientId, setClientId] = React.useState<string | undefined>(undefined);

  // Only generate an ID on the client side (after mounting)
  React.useEffect(() => {
    if (!id) {
      setClientId(generateId());
    }
  }, [id]);

  // Use the provided `id` if available, otherwise use the generated one
  const finalId = id || clientId;

  return (
    <TabsPrimitive.Root
      ref={ref}
      id={finalId}
      className={cn("relative", className)}
      {...props}
    />
  );
});

Tabs.displayName = "Tabs";

// TabsList, TabsTrigger, and TabsContent remain the same
const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    variant?: 'default' | 'pills' | 'underline';
  }
>(({ className, variant = 'default', ...props }, ref) => {
  const variantStyles = {
    // add sticky, top and z-index + background
    default: "sticky top-0 z-10 inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground",
    pills: "sticky top-0 z-10 inline-flex h-10 items-center justify-start gap-2 bg-transparent p-0 text-muted-foreground",
    underline: "sticky top-0 z-5 inline-flex h-10 items-center justify-start gap-0 bg-transparent border-b border-border p-0 text-muted-foreground"
  };

  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(variantStyles[variant], className)}
      {...props}
    />
  );
});

TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    variant?: 'default' | 'pills' | 'underline';
  }
>(({ className, variant = 'default', ...props }, ref) => {
  const variantStyles = {
    default: cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium",
      "ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2",
      "focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
      "data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
    ),
    pills: cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium",
      "transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      "disabled:pointer-events-none disabled:opacity-50 hover:bg-muted/50",
      "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm"
    ),
    underline: cn(
      "inline-flex items-center justify-center whitespace-nowrap px-4 py-2 text-sm font-medium",
      "border-b-2 border-transparent transition-all focus-visible:outline-none",
      "focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
      "hover:text-foreground data-[state=active]:border-primary data-[state=active]:text-foreground"
    )
  };

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(variantStyles[variant], className)}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "my-1 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sticky",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
